import styled, { css } from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { Spinner } from "pro-features/components/Loaders/src/Spinner";

import {
    GROWTH_OVERVIEW_COLUMN_WIDTHS,
    GROWTH_OVERVIEW_EXPANDED_ROW_ACCENT,
    GROWTH_OVERVIEW_EXPANDED_ROW_HIGHLIGHT,
} from "../const";

const { name: NAME_WIDTH, summary: SUMMARY_WIDTH, period: PERIOD_WIDTH } = GROWTH_OVERVIEW_COLUMN_WIDTHS;

export const GROWTH_OVERVIEW_HEADER_CELL_HEIGHT = 48;

export const GROWTH_OVERVIEW_TABLE_Z_INDEX = {
    period: 1,
    headerUnpinned: 2,
    stickyName: 3,
    stickySummary: 4,
    headerPinned: 5,
    showAllRow: 7,
    scrollButton: 7,
    /** Above sticky headers (5); chevron buttons use scrollButton. */
    scrollSteppers: 9,
} as const;

export const GrowthOverviewTableContainer = styled.div`
    width: 100%;
    height: fit-content;
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 8px;
    overflow: visible;
    background: ${colorsPalettes.carbon[0]};
`;

export const GrowthOverviewTableLoader = styled.div`
    overflow: hidden;
`;

export const GrowthOverviewTableScrollArea = styled.div`
    overflow: auto;
    overscroll-behavior-y: contain;
    scroll-padding-top: ${GROWTH_OVERVIEW_HEADER_CELL_HEIGHT}px;
    width: 100%;
    height: fit-content;
    max-height: calc(100vh - 180px);
    isolation: isolate;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${colorsPalettes.carbon[100]};
        border-radius: 4px;
    }
`;

export const GrowthOverviewGrid = styled.div<{ $periodCount: number }>`
    display: grid;
    width: 100%;
    grid-template-columns:
        ${NAME_WIDTH}px ${SUMMARY_WIDTH}px
        repeat(${({ $periodCount }) => $periodCount}, minmax(${PERIOD_WIDTH}px, 1fr));
    min-width: min-content;
`;

const cellBorderStyles = css`
    border-bottom: 1px solid ${colorsPalettes.carbon[50]};
    border-right: 1px solid ${colorsPalettes.carbon[25]};
`;

const resetButtonBorderStyles = css`
    border: none;
    ${cellBorderStyles}
`;

const cellBaseStyles = css`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    ${cellBorderStyles}
    min-height: ${GROWTH_OVERVIEW_HEADER_CELL_HEIGHT}px;
    box-sizing: border-box;
`;

const nestDepthPadding = ($nestDepth?: number) =>
    $nestDepth != null &&
    $nestDepth > 0 &&
    css`
        padding-left: ${12 + $nestDepth * 24}px;
    `;

export const GROWTH_OVERVIEW_ROW_HOVER_CLASS = "growth-overview-row-hovered";

const getStaticCellBackground = (highlighted?: boolean): string =>
    highlighted ? GROWTH_OVERVIEW_EXPANDED_ROW_HIGHLIGHT : colorsPalettes.carbon[0];

const rowHoverStyles = css`
    &.${GROWTH_OVERVIEW_ROW_HOVER_CLASS} {
        background-color: ${colorsPalettes.carbon[25]};
    }

    &.${GROWTH_OVERVIEW_ROW_HOVER_CLASS} > * {
        background-color: ${colorsPalettes.carbon[25]} !important;
    }
`;

export const GrowthOverviewHeaderCell = styled.button<{
    $stickyLeft?: number;
    $alignEnd?: boolean;
    $alignCenter?: boolean;
    $isIncomplete?: boolean;
}>`
    ${cellBaseStyles}
    ${resetButtonBorderStyles}
    font: inherit;
    width: 100%;
    min-width: 0;
    cursor: pointer;
    font-weight: 600;
    color: ${colorsPalettes.carbon[400]};
    position: sticky;
    top: 0;
    z-index: ${({ $stickyLeft }) =>
        $stickyLeft != null
            ? GROWTH_OVERVIEW_TABLE_Z_INDEX.headerPinned
            : GROWTH_OVERVIEW_TABLE_Z_INDEX.headerUnpinned};
    background-color: ${colorsPalettes.carbon[25]};

    ${({ $stickyLeft }) =>
        $stickyLeft != null &&
        css`
            left: ${$stickyLeft}px;
        `}

    ${({ $stickyLeft }) =>
        $stickyLeft === NAME_WIDTH &&
        css`
            box-shadow: 6px 0 8px -6px ${colorsPalettes.carbon[100]};
        `}

    ${({ $alignEnd }) =>
        $alignEnd &&
        css`
            justify-content: flex-end;

            .headerCell-text {
                text-align: right;
            }
        `}

    ${({ $alignCenter, $isIncomplete }) =>
        $alignCenter &&
        css`
            justify-content: center;

            .headerCell-text {
                flex: 0 1 auto;
                text-align: center;

                ${!$isIncomplete &&
                css`
                    overflow: visible;
                    text-overflow: clip;
                `}
            }
        `}

    .headerCell-text {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .headerCell-sortDirection {
        margin: 0;
        flex-shrink: 0;
        visibility: hidden;
    }

    &.is-sortable:hover .headerCell-sortDirection,
    &.is-sorted .headerCell-sortDirection {
        visibility: visible;
    }
`;

const growthOverviewNameCellLayoutStyles = css<{
    $nestDepth?: number;
    $highlighted?: boolean;
}>`
    ${cellBaseStyles}
    ${rowHoverStyles}
    position: sticky;
    left: 0;
    z-index: ${GROWTH_OVERVIEW_TABLE_Z_INDEX.stickyName};
    background-color: ${({ $highlighted }) => getStaticCellBackground($highlighted)};

    ${({ $nestDepth }) => nestDepthPadding($nestDepth)}
`;

export const GrowthOverviewNameCellButton = styled.button<{
    $nestDepth?: number;
    $highlighted?: boolean;
}>`
    ${growthOverviewNameCellLayoutStyles}
    ${resetButtonBorderStyles}
    font: inherit;
    width: 100%;
    text-align: left;
    cursor: pointer;

    ${({ $highlighted }) =>
        $highlighted &&
        css`
            box-shadow: inset 4px 0 0 ${GROWTH_OVERVIEW_EXPANDED_ROW_ACCENT};
        `}
`;

export const GrowthOverviewNameCell = styled.div<{
    $nestDepth?: number;
    $highlighted?: boolean;
}>`
    ${growthOverviewNameCellLayoutStyles}
`;

export const GrowthOverviewSummaryCell = styled.div<{
    $highlighted?: boolean;
    $alignEnd?: boolean;
}>`
    ${cellBaseStyles}
    ${rowHoverStyles}
    position: sticky;
    left: ${NAME_WIDTH}px;
    z-index: ${GROWTH_OVERVIEW_TABLE_Z_INDEX.stickySummary};
    box-shadow: 6px 0 8px -6px ${colorsPalettes.carbon[100]};
    background-color: ${({ $highlighted }) => getStaticCellBackground($highlighted)};

    ${({ $alignEnd }) =>
        $alignEnd &&
        css`
            justify-content: flex-end;
        `}
`;

export const GrowthOverviewPeriodCell = styled.div`
    ${cellBaseStyles}
    ${rowHoverStyles}
    padding: 0;
    z-index: ${GROWTH_OVERVIEW_TABLE_Z_INDEX.period};
    align-items: stretch;

    & > * {
        flex: 1;
        width: 100%;
        min-width: 0;
    }
`;

export const GrowthOverviewShowAllCellButton = styled.button<{
    $nestDepth?: number;
}>`
    ${cellBaseStyles}
    ${rowHoverStyles}
    ${resetButtonBorderStyles}
    font: inherit;
    width: 100%;
    text-align: left;
    cursor: pointer;
    white-space: nowrap;
    position: sticky;
    left: 0;
    z-index: ${GROWTH_OVERVIEW_TABLE_Z_INDEX.showAllRow};
    background-color: ${colorsPalettes.carbon[0]};

    ${({ $nestDepth }) => nestDepthPadding($nestDepth)}
`;

export const GrowthOverviewNoData = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 48px 24px;
    text-align: center;
`;

export const ExpandIcon = styled.span`
    display: inline-flex;
    width: 16px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    user-select: none;
`;

export const GrowthOverviewExpandSpinner = styled(Spinner)`
    width: 12px;
    height: 12px;
    border-width: 2px;
`;

export const ShowAllDomainsLabel = styled(Text).attrs({
    fontVariant: "input2",
})`
    color: ${colorsPalettes.blue[400]};
    white-space: nowrap;
`;

export const GrowthOverviewSearchHighlight = styled.span`
    background-color: ${colorsPalettes.blue[100]};
`;

export const GrowthOverviewRowLabel = styled(Text).attrs({
    fontVariant: "input2",
})<{ $rowType: "industry" | "sub-industry" }>`
    ${({ $rowType }) =>
        $rowType === "industry" &&
        css`
            font-weight: 600;
        `}
`;
